
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __appWithI18n from 'next-translate/appWithI18n'
// @ts-ignore
    
/* eslint-disable @next/next/no-before-interactive-script-outside-document */
import { IdProvider } from '@radix-ui/react-id'
import { fbVisit } from 'api/analytics/analytics'
import { ShoppingCartSidebarProvider } from 'contexts/ShoppingCartSidebarContext'
import { ToastProvider } from 'contexts/ToastContext'
import { usePersistLocaleCookie } from 'hooks/usePersistedLocale'
import { useLocationSearch } from 'hooks/useRouter'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { useEffect, useRef, useState } from 'react'
import { QueryClient, QueryClientProvider, setLogger } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import { Hydrate, type DehydratedState } from 'react-query/hydration'
import { CookieKeys, getCookie, removeCookie, getVidCookie, setVidCookie } from 'services/Cookies'
import { Storage, StorageKeys } from 'services/Storage'
import { globalStyles } from 'stitches.config'
import { Api, Errors } from 'types/Api'
import { DiscountBanner } from '../components/DiscountBanner'
import { SessionKeys, SessionStorage } from '../services/Session'
import { ENV } from '../constants/EnvHelp'
import { DiscountCodeProvider } from '../contexts/DiscountContext'
import {
  popElementFromParams,
  concatElementsFromParams,
  existsInParamsCheck,
} from 'utils/queryParams'

function MyApp({ Component, pageProps }: AppProps<{ dehydratedState: DehydratedState }>) {
  const queryClientRef = useRef<QueryClient>()
  const router = useRouter()

  useEffect(() => {
    const _listener = (event: any) => {
      if ((document.activeElement as HTMLInputElement).type === 'number') {
        ;(document.activeElement as HTMLInputElement).blur()
      }
    }

    document.addEventListener('wheel', _listener)

    return () => document.removeEventListener('wheel', _listener)
  }, [])

  globalStyles()
  usePersistLocaleCookie()

  if (!queryClientRef.current) {
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          onError: (err) => {
            const error = err as Api.Error
            if (
              (error &&
                error.statusCode === 401 &&
                (error.message === Errors.JWT_EXPIRED ||
                  error.message === Errors.INVALID_SIGNATURE)) ||
              (error && error.statusCode === 404 && error.code === Errors.CLIENT_NOT_FOUND)
            ) {
              removeCookie(CookieKeys.SESSION)
              router.replace('/login')
            }
          },
        },
        mutations: {
          onError: (err) => {
            const error = err as Api.Error
            if (
              (error &&
                error.statusCode === 401 &&
                (error.message === Errors.JWT_EXPIRED ||
                  error.message === Errors.INVALID_SIGNATURE)) ||
              (error.statusCode === 404 && error.code === Errors.CLIENT_NOT_FOUND)
            ) {
              removeCookie(CookieKeys.SESSION)
              router.replace('/login')
            }
          },
        },
      },
    })

    queryClientRef.current = queryClient
  }

  setLogger({
    log: () => {},
    warn: () => {},
    error: () => {},
  })

  //search for query params
  const search = useLocationSearch()
  const [ranUTMS, setRanUTMS] = useState(false)
  const [sessionId, setsessionId] = useState(false)

  const allowedConfigurations = ['08_25_REV5B']

  useEffect(() => {
    if (
      existsInParamsCheck('utm_source', search) &&
      !SessionStorage().getItem(SessionKeys.UTM_SOURCE)
    )
      sessionStoreUtmSourceIn(search)
    if (existsInParamsCheck('utm_', search) && !Storage.get(StorageKeys.UTM_KEYS)) {
      const utmParamValues = concatElementsFromParams('utm_', search)
      Storage.set(StorageKeys.UTM_KEYS, utmParamValues)
    }
    if (
      existsInParamsCheck('irclickid', search) &&
      !Storage.get(StorageKeys.IMPACT_RADIUS_CLICK_ID)
    ) {
      let ircId = popElementFromParams('irclickid', search)
      if (ircId) {
        Storage.set(StorageKeys.IMPACT_RADIUS_CLICK_ID, ircId)
      }
    }
    if (
      !Storage.get(StorageKeys.VERSION_FLAG) ||
      typeof Storage.get(StorageKeys.VERSION_FLAG) !== 'object' ||
      illegalFlagFound(Storage.get(StorageKeys.VERSION_FLAG)) ||
      requiredTestMissing(Storage.get(StorageKeys.VERSION_FLAG))
    ) {
      Storage.set(StorageKeys.APPLE_PAY_ELIGIBLE, window.ApplePaySession ? 'true' : 'false') //never remove this

      const creationConfiguration = allowedConfigurations[0]

      Storage.set(StorageKeys.VERSION_FLAG, {
        creationConfiguration:
          ENV.getVersionFlags()?.creationConfiguration || creationConfiguration,
      })
    }

    if (existsInParamsCheck('test', search) && existsInParamsCheck('variant', search)) {
      const key = popElementFromParams('test', search)
      const value = popElementFromParams('variant', search)
      const obj = {} as any
      if (key) obj[key] = value
      Storage.set(StorageKeys.VERSION_FLAG, obj)
    }

    // utilized to set cookies for server side variables in testing. keeping for reference
    // if (Storage.get(StorageKeys.VERSION_FLAG)) {
    //   const recipeSelectorStored = Storage.get(StorageKeys.VERSION_FLAG)?.recipeSelector
    //   setCookie(CookieKeys.RECIPE_SELECTOR, recipeSelectorStored)
    // }

    if (window) {
      window.onload = (e: any) => {
        Storage.set(StorageKeys.APPLE_PAY_ELIGIBLE, window.ApplePaySession ? 'true' : 'false')
      }

      window.dataLayer.push({
        env: ENV.isStaging ? 'staging' : ENV.isBeta ? 'beta' : ENV.isProd ? 'production' : '',
      })
    }

    setRanUTMS(true)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!ranUTMS) return

    const cookieVID = getVidCookie(CookieKeys._VID)

    // If cookie exists, decode and validate it
    if (cookieVID) {
      try {
        const decodedData = JSON.parse(decodeURIComponent(cookieVID))

        // Check if the decoded cookie contains valid value and expiry
        if (decodedData.value && decodedData.expiry) {
          const now = Date.now()
          if (decodedData.expiry > now) {
            setsessionId(true)
            return
          }
        }
      } catch {
        // If parsing fails, fall through to creating new VID
      }
    }

    // Create new VID if no valid cookie exists or if the cookie has expired
    const visitorId = Number.parseInt(Date.now() + Math.floor(Math.random() * 10000).toString())
    const vidData = {
      value: visitorId,
      expiry: new Date(Date.now() + 6 * 60 * 60 * 1000).getTime(), // Expiry set for 6 hours from now
    }

    // Set the cookie with the domain specified to allow access across subdomains
    setVidCookie(CookieKeys._VID, JSON.stringify(vidData), '.wefeedraw.com') // Cookie valid for 1 day (24 hours)
    setsessionId(true)
  }, [ranUTMS])

  useEffect(() => {
    if (!window || !sessionId) return
    const sessionStorage = SessionStorage()
    window.onload = (e: any) => {
      const gaCookie = getCookie(CookieKeys.GA)
      if (gaCookie) sessionStorage.setItem(SessionKeys.GA, gaCookie)
      Storage.set(StorageKeys.APPLE_PAY_ELIGIBLE, window.ApplePaySession ? 'true' : 'false')
    }
    ;(async () => {
      await fbVisit()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  const illegalFlagFound = (obj: any): boolean => {
    if (!obj) return false
    const allowedFlags: string[] = allowedConfigurations

    const keys = Object.keys(obj)
    const filter = keys.filter((e) => {
      return !allowedFlags.includes(obj[e])
    })

    return filter.length > 0
  }

  const requiredTestMissing = (obj: any): boolean => {
    if (!obj) return false
    const requiredFlags: string[] = ['creationConfiguration']

    const keys = Object.keys(obj)
    const filter = requiredFlags.filter((e) => {
      return !keys.includes(e)
    })
    return filter.length > 0
  }

  const sessionStoreUtmSourceIn = (search: string | undefined) => {
    const allowedSources = ['future_subscription_reminder', 'subscription_renewal_reminder']
    const arr = search && search.toLowerCase().replace('?', '').split('&')
    if (!arr) return
    for (let i = 0; i < arr.length; i++) {
      if (!arr[i].includes('utm_source')) continue
      const value = arr[i].split('=').pop() || ''
      if (!allowedSources.includes(value)) continue
      SessionStorage().setItem(SessionKeys.UTM_SOURCE, value)
    }
  }

  return (
    <QueryClientProvider client={queryClientRef.current}>
      <Script src="https://js.chargebee.com/v2/chargebee.js" />
      <Hydrate state={pageProps.dehydratedState}>
        <IdProvider>
          <ToastProvider>
            <ShoppingCartSidebarProvider>
              <DiscountCodeProvider>
                <DiscountBanner pathName={router.pathname} />
                <Component {...pageProps} />
              </DiscountCodeProvider>
            </ShoppingCartSidebarProvider>
          </ToastProvider>
        </IdProvider>
      </Hydrate>
      <ReactQueryDevtools />
    </QueryClientProvider>
  )
}

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// MyApp.getInitialProps = async (appContext: AppContext) => {
//   // calls page's `getInitialProps` and fills `appProps.pageProps`
//   const appProps = await App.getInitialProps(appContext);

//   return { ...appProps }
// }

const __Page_Next_Translate__ = MyApp


// @ts-ignore
    export default __appWithI18n(__Page_Next_Translate__, {
// @ts-ignore
      ...__i18nConfig,
// @ts-ignore
      isLoader: true,
// @ts-ignore
      skipInitialProps: false,
// @ts-ignore
      
// @ts-ignore
    });
// @ts-ignore
  